import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { debounceTime, Subject, takeUntil } from 'rxjs';
import { PessoaDTO } from '../../../models/clientes/pessoa.model';
import { PessoaRucResultado } from '../../../models/pessoas/pessoa-ruc-resultado.model';
import { PaisDDI } from '../../../models/cardapio-digital/cardapio-digital.model';
import { PAISES_DDI } from '../../data/paises-ddi.data';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { CidadeService } from '../../../services/configuracoes/cidade.service';
import { RucBuscaComponent } from '../ruc-busca/ruc-busca.component';
import { extrairMensagemErroHttp } from '../../utils/error-utils';

@Component({
  selector: 'app-pessoa-adicionar-dialog',
  templateUrl: './pessoa-adicionar-dialog.component.html',
  styleUrls: ['./pessoa-adicionar-dialog.component.scss'],
})
export class PessoaAdicionarDialogComponent implements OnChanges, OnDestroy {

  // === INPUTS ===

  /** Controle de visibilidade do dialog (two-way binding) */
  @Input() visible = false;

  /** Tipo de pessoa: define categoriaCliente/categoriaFornecedor no POST */
  @Input() tipo: 'cliente' | 'fornecedor' = 'cliente';

  // === OUTPUTS ===

  /** Emitido quando visible muda (two-way binding) */
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emitido quando uma pessoa NOVA foi cadastrada com sucesso via POST */
  @Output() pessoaCriada = new EventEmitter<PessoaDTO>();

  /** Emitido quando uma pessoa JA EXISTENTE foi encontrada via RUC interno */
  @Output() pessoaVinculada = new EventEmitter<PessoaDTO>();

  // === PROPRIEDADES INTERNAS ===

  @ViewChild(RucBuscaComponent) rucBuscaComponent?: RucBuscaComponent;

  /** Modo de busca: 'ruc' ou 'ci' */
  modoBusca: 'ruc' | 'ci' = 'ruc';

  rucBusca = '';
  nome = '';
  telefone = '';
  paisSelecionado: PaisDDI = PAISES_DDI[0];
  cidadeList: { id: number; nome: string; nomeEstado?: string; displayLabel: string }[] = [];
  cidadeSelecionada: any = null;
  cidadeCarregando = false;
  rucEncontrado = false;
  salvando = false;
  tentouSalvar = false;

  /** Campos para busca por CI */
  ciBusca = '';
  ciBuscando = false;
  ciEncontrado = false;
  ciErro: string | null = null;

  /** Pessoa encontrada na API interna (ja cadastrada) — aguarda confirmacao do usuario */
  private pessoaPendenteVinculacao: PessoaDTO | null = null;

  /** Subject para debounce da busca por CI */
  private ciInput$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private cidadeService: CidadeService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && changes['visible'].currentValue === true) {
      this.resetar();
      this.carregarCidades();
      this.inicializarDebounceCI();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // === METODOS PUBLICOS (para o pai usar via @ViewChild) ===

  /** Permite que o componente pai dispare o submit via @ViewChild */
  public submitExterno(): void {
    this.submit();
  }

  /** Permite que o componente pai dispare o reset */
  public resetar(): void {
    this.modoBusca = 'ruc';
    this.rucBusca = '';
    this.ciBusca = '';
    this.nome = '';
    this.telefone = '';
    this.paisSelecionado = PAISES_DDI[0];
    this.cidadeSelecionada = null;
    this.rucEncontrado = false;
    this.ciEncontrado = false;
    this.ciBuscando = false;
    this.ciErro = null;
    this.salvando = false;
    this.tentouSalvar = false;
    this.pessoaPendenteVinculacao = null;
    this.rucBuscaComponent?.reset();
  }

  // === HANDLERS DO RUC-BUSCA ===

  /** Handler: campo RUC foi limpo — resetar campos preenchidos automaticamente */
  onRucLimpo(): void {
    this.nome = '';
    this.telefone = '';
    this.paisSelecionado = PAISES_DDI[0];
    this.cidadeSelecionada = null;
    this.rucEncontrado = false;
    this.pessoaPendenteVinculacao = null;
  }

  /** Handler: pessoa encontrada na API interna (ja cadastrada) — preencher campos e aguardar confirmacao */
  onPessoaVinculada(pessoa: PessoaDTO): void {
    this.pessoaPendenteVinculacao = pessoa;
    this.rucBusca = (pessoa as any).ruc || '';
    this.nome = pessoa.nome || '';
    this.telefone = (pessoa as any).telefone || '';
    // Preencher DDI se disponivel
    const ddi = (pessoa as any).ddi;
    if (ddi) {
      const ddiComPlus = String(ddi).startsWith('+') ? String(ddi) : `+${ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) {
        this.paisSelecionado = paisEncontrado;
      }
    }
    this.rucEncontrado = true;
    // Preencher cidade se disponivel (API retorna objeto cidade com id, nao cidade_id)
    const cidadeId = (pessoa as any).cidade?.id || (pessoa as any).cidade_id || (pessoa as any).cidadeId;
    if (cidadeId) {
      if (this.cidadeList.length === 0) {
        setTimeout(() => {
          this.cidadeSelecionada = this.cidadeList.find(c => c.id === cidadeId) || null;
        }, 1000);
      } else {
        this.cidadeSelecionada = this.cidadeList.find(c => c.id === cidadeId) || null;
      }
    }
  }

  /** Handler: pessoa encontrada na API externa (sem id) — preencher campos do formulario */
  onPessoaConfirmada(resultado: PessoaRucResultado): void {
    this.rucBusca = resultado.ruc || '';
    this.nome = resultado.nome || '';
    this.telefone = resultado.telefone || '';
    // Preencher DDI se disponivel
    if (resultado.ddi) {
      const ddiComPlus = resultado.ddi.startsWith('+') ? resultado.ddi : `+${resultado.ddi}`;
      const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
      if (paisEncontrado) {
        this.paisSelecionado = paisEncontrado;
      }
    }
    this.rucEncontrado = true;
    // Buscar cidade pelo id
    if (resultado.cidade && resultado.cidade.id) {
      if (this.cidadeList.length === 0) {
        setTimeout(() => {
          this.cidadeSelecionada = this.cidadeList.find(c => c.id === resultado.cidade?.id) || null;
        }, 1000);
      } else {
        this.cidadeSelecionada = this.cidadeList.find(c => c.id === resultado.cidade?.id) || null;
      }
    }
  }

  // === HANDLER DE TROCA DE MODO ===

  /** Ao trocar entre RUC e CI, limpar campos e resetar estado */
  onModoBuscaChange(): void {
    this.rucBusca = '';
    this.ciBusca = '';
    this.nome = '';
    this.telefone = '';
    this.paisSelecionado = PAISES_DDI[0];
    this.cidadeSelecionada = null;
    this.rucEncontrado = false;
    this.ciEncontrado = false;
    this.ciBuscando = false;
    this.ciErro = null;
    this.pessoaPendenteVinculacao = null;
    this.rucBuscaComponent?.reset();
  }

  // === HANDLERS DO CI ===

  /** Filtra caracteres e emite para o Subject de debounce (CI) */
  onCiInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    // Aceitar apenas numeros e letras
    const filtrado = (input.value || '').replace(/[^a-zA-Z0-9]/g, '');
    if (filtrado !== input.value) {
      input.value = filtrado;
    }
    this.ciBusca = filtrado;
    this.ciErro = null;

    // Se campo foi limpo, resetar estado
    if (!filtrado || filtrado.trim().length === 0) {
      this.ciEncontrado = false;
      this.nome = '';
      this.telefone = '';
      this.paisSelecionado = PAISES_DDI[0];
      this.cidadeSelecionada = null;
      this.pessoaPendenteVinculacao = null;
      return;
    }

    this.ciInput$.next(filtrado.trim());
  }

  // === SUBMIT ===

  submit(): void {
    this.tentouSalvar = true;
    if (!this.nome) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translate.instant('common.atencao'),
        detail: this.translate.instant('pessoa-adicionar-dialog.mensagens.campos_obrigatorios'),
      });
      return;
    }

    // Pessoa ja cadastrada na API interna — vincular direto sem POST
    if (this.pessoaPendenteVinculacao) {
      this.pessoaVinculada.emit(this.pessoaPendenteVinculacao);
      const chaveMsg = this.tipo === 'cliente'
        ? 'pessoa-adicionar-dialog.mensagens.cliente_vinculado'
        : 'pessoa-adicionar-dialog.mensagens.fornecedor_vinculado';
      this.messageService.add({
        severity: 'success',
        summary: this.translate.instant('common.sucesso'),
        detail: this.translate.instant(chaveMsg),
      });
      this.resetar();
      this.fechar();
      return;
    }

    this.salvando = true;

    const tipoPessoaDefault = this.tipo === 'cliente' ? 'FISICA' : 'JURIDICA';

    const body: any = {
      nome: this.nome,
      fantasia: this.nome,
      ddi: this.telefone ? this.paisSelecionado.ddi.replace('+', '') : null,
      telefone: this.telefone ? this.telefone.replace(/\D/g, '') : null,
      tipoPessoa: tipoPessoaDefault,
      sexo: 'MASCULINO',
      tipoNaturezaFiscal: 'NAO_CONTRIBUINTE',
      email: '',
      categoriaCliente: this.tipo === 'cliente',
      categoriaFornecedor: this.tipo === 'fornecedor',
      categoriaVendedor: false,
      // `cidade_id`, NUNCA `cidade: {id}`. O GET devolve `cidade` como OBJETO
      // (PessoaConsultaDTO), mas o POST/PUT so aceita `cidade_id` (PessoaDTO.kt:54).
      // Mandar o objeto fazia o Jackson descartar a chave em silencio (sem 400) e a
      // pessoa nascia sem cidade — quebrando a fatura legal depois, em
      // PessoaService.verificarDadosDaPessoaFaturaLegal.
      cidade_id: this.cidadeSelecionada?.id ?? null,
      ruc: this.modoBusca === 'ruc' && this.rucBusca ? this.rucBusca : null,
      ci: this.modoBusca === 'ci' && this.ciBusca ? this.ciBusca : null,
    };

    // Enviar ruc ou ci conforme o modo selecionado
    if (this.modoBusca === 'ci') {
      body.ci = this.ciBusca;
    } else {
      body.ruc = this.rucBusca;
    }

    this.pessoaService.post('/pessoas', body).subscribe({
      next: (pessoaCriada: any) => {
        this.salvando = false;
        const chaveMsg = this.tipo === 'cliente'
          ? 'pessoa-adicionar-dialog.mensagens.cliente_cadastrado'
          : 'pessoa-adicionar-dialog.mensagens.fornecedor_cadastrado';
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant(chaveMsg),
        });
        this.pessoaCriada.emit(pessoaCriada);
        this.resetar();
        this.fechar();
      },
      error: (err: any) => {
        this.salvando = false;
        const erros = err?.error?.errors;
        let detail = '';
        if (erros && erros.length > 0) {
          detail = erros.map((e: any) => e.mensagem).join('. ');
        } else {
          detail = extrairMensagemErroHttp(err, this.translate.instant('pessoa-adicionar-dialog.mensagens.erro_cadastrar'));
        }
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail,
        });
      },
    });
  }

  // === GETTERS PARA I18N ===

  get tituloDialog(): string {
    return this.tipo === 'cliente'
      ? this.translate.instant('pessoa-adicionar-dialog.titulos.adicionar_cliente')
      : this.translate.instant('pessoa-adicionar-dialog.titulos.adicionar_fornecedor');
  }

  get descricaoDialog(): string {
    return this.tipo === 'cliente'
      ? this.translate.instant('pessoa-adicionar-dialog.descricoes.adicionar_cliente')
      : this.translate.instant('pessoa-adicionar-dialog.descricoes.adicionar_fornecedor');
  }

  get labelNome(): string {
    return this.tipo === 'cliente'
      ? this.translate.instant('pessoa-adicionar-dialog.labels.nome_cliente')
      : this.translate.instant('pessoa-adicionar-dialog.labels.nome_fornecedor');
  }

  // === METODOS PRIVADOS ===

  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  /** Inicializa o Subject de debounce para busca por CI */
  private inicializarDebounceCI(): void {
    // Limpar subscricao anterior para evitar duplicacao
    this.destroy$.next();
    this.destroy$ = new Subject<void>();
    this.ciInput$ = new Subject<string>();

    this.ciInput$.pipe(
      debounceTime(1500),
      takeUntil(this.destroy$),
    ).subscribe(ci => this.buscarPorCI(ci));
  }

  /** Busca pessoa na API interna por CI */
  private buscarPorCI(ci: string): void {
    if (!ci || ci.length === 0) return;

    // Se o campo foi limpo enquanto o debounce esperava, ignorar busca
    if (!this.ciBusca || this.ciBusca.trim().length === 0) return;

    this.ciBuscando = true;
    this.ciEncontrado = false;
    this.ciErro = null;

    const categoriaParam = this.tipo === 'cliente' ? 'categoriaCliente' : 'categoriaFornecedor';

    this.pessoaService.get(`/pessoas/buscar?ci=${ci}&${categoriaParam}=true&size=1`).subscribe({
      next: (res: any) => {
        this.ciBuscando = false;
        const resultados = res?.content || [];
        if (resultados.length > 0) {
          const pessoa = resultados[0];
          this.ciEncontrado = true;
          this.pessoaPendenteVinculacao = pessoa;
          this.ciBusca = (pessoa as any).ci || ci;
          this.nome = pessoa.nome || '';
          this.telefone = (pessoa as any).telefone || '';
          // Preencher DDI se disponivel
          const ddi = (pessoa as any).ddi;
          if (ddi) {
            const ddiComPlus = String(ddi).startsWith('+') ? String(ddi) : `+${ddi}`;
            const paisEncontrado = PAISES_DDI.find(p => p.ddi === ddiComPlus);
            if (paisEncontrado) {
              this.paisSelecionado = paisEncontrado;
            }
          }
          // Preencher cidade se disponivel
          const cidadeId = (pessoa as any).cidade?.id || (pessoa as any).cidade_id || (pessoa as any).cidadeId;
          if (cidadeId) {
            if (this.cidadeList.length === 0) {
              setTimeout(() => {
                this.cidadeSelecionada = this.cidadeList.find(c => c.id === cidadeId) || null;
              }, 1000);
            } else {
              this.cidadeSelecionada = this.cidadeList.find(c => c.id === cidadeId) || null;
            }
          }
        } else {
          // Nenhuma pessoa encontrada — usuario pode preencher manualmente
          this.ciEncontrado = false;
          this.pessoaPendenteVinculacao = null;
        }
      },
      error: () => {
        this.ciBuscando = false;
        this.ciErro = this.translate.instant('pessoa-adicionar-dialog.mensagens.erro_buscar_ci');
      },
    });
  }

  private carregarCidades(): void {
    if (this.cidadeList.length > 0) return;

    this.cidadeCarregando = true;
    this.cidadeService.get('/cidade?listarTodos=true').subscribe({
      next: (response: any) => {
        this.cidadeList = (response.content || []).map((c: any) => ({
          id: c.id,
          nome: c.nome,
          nomeEstado: c.nomeEstado || null,
          displayLabel: c.nomeEstado ? `${c.nome} - ${c.nomeEstado}` : c.nome,
        }));
        this.cidadeCarregando = false;
      },
      error: () => {
        this.cidadeCarregando = false;
      },
    });
  }
}
